/**
 * Florame cart liquid interactions.
 * Presentation only: Shopify and Delivery Scheduler keep ownership of cart state.
 * v3.0.628 · 2026-08-31
 */
(function () {
  'use strict';

  var CART_ROOT_SELECTOR = '#main-cart-items';
  var SURFACE_SELECTOR = [
    '.florame-cart-line',
    '.ds-app-wrapper',
    '.ds-intent-launch',
    '.ds-intent-summary',
    '#main-cart-footer .cart__blocks'
  ].join(',');
  var RIPPLE_SELECTOR = [
    '.cart__checkout-button',
    '.ds-nav-btn',
    '.ds-intent-launch',
    '.ds-intent-summary',
    '.quantity__button',
    'cart-remove-button .button',
    '[data-add-upsell]',
    'button[data-essential-upsell-element="add-to-cart-button"]'
  ].join(',');
  var PRESS_SELECTOR = [
    '.florame-cart-back',
    '.cart__checkout-button',
    '.florame-checkout-trust',
    '.ds-nav-btn',
    '.ds-delivery-mode-btn',
    '.ds-ticket-mode-btn',
    '.ds-intent-launch',
    '.ds-intent-summary',
    '.quantity__button',
    'cart-remove-button .button',
    '[data-add-upsell]',
    'button[data-essential-upsell-element="add-to-cart-button"]'
  ].join(',');

  var frame = 0;
  var countFrame = 0;
  var pendingSurface = null;
  var pendingPoint = null;
  var pressedControl = null;
  var pressedSurface = null;
  var countAnimationTimer = 0;
  var entrancePlayed = false;

  function playInitialEntrance() {
    if (entrancePlayed) return;
    entrancePlayed = true;
    if (reducedMotion() || document.hidden) return;
    // Animate only the initial visible surfaces. Shopify replacements stay opaque.
    var surfaces = Array.from(document.querySelectorAll('.florame-cart-line, .ds-app-wrapper, #main-cart-footer .cart__blocks'))
      .filter(function (node) { var rect=node.getBoundingClientRect(); return rect.height>0 && rect.top<innerHeight && rect.bottom>0; }).slice(0,6);
    surfaces.forEach(function (node,index) {
      if (!node.animate) return;
      node.animate([{opacity:.82,translate:'0 7px'},{opacity:1,translate:'0 0'}], {
        duration:240,delay:index*30,easing:'cubic-bezier(.2,.8,.2,1)'
      });
    });
  }

  function isCartPage() {
    return Boolean(document.querySelector(CART_ROOT_SELECTOR));
  }

  function reducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function updateLiquidLight() {
    frame = 0;
    if (!pendingSurface || !pendingPoint || !pendingSurface.isConnected) return;

    var rect = pendingSurface.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var x = Math.max(0, Math.min(100, ((pendingPoint.x - rect.left) / rect.width) * 100));
    var y = Math.max(0, Math.min(100, ((pendingPoint.y - rect.top) / rect.height) * 100));
    pendingSurface.style.setProperty('--glass-x', x.toFixed(2) + '%');
    pendingSurface.style.setProperty('--glass-y', y.toFixed(2) + '%');

    if (!reducedMotion() && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      var strength = pendingSurface.matches('.ds-app-wrapper, #main-cart-footer .cart__blocks') ? 0.42 : 1;
      pendingSurface.style.setProperty('--glass-tilt-x', (((0.5 - (y / 100)) * 1.8) * strength).toFixed(2) + 'deg');
      pendingSurface.style.setProperty('--glass-tilt-y', ((((x / 100) - 0.5) * 2.2) * strength).toFixed(2) + 'deg');
    }
  }

  function queueLiquidLight(surface, clientX, clientY) {
    pendingSurface = surface;
    pendingPoint = { x: clientX, y: clientY };
    if (!frame) frame = window.requestAnimationFrame(updateLiquidLight);
  }

  function createRipple(control, clientX, clientY) {
    if (!control || reducedMotion()) return;

    var style = window.getComputedStyle(control);
    if (style.position === 'static') control.style.position = 'relative';
    if (style.overflow === 'visible') control.style.overflow = 'hidden';

    var rect = control.getBoundingClientRect();
    var ripple = document.createElement('span');
    ripple.className = 'florame-liquid-ripple';
    ripple.setAttribute('aria-hidden', 'true');
    ripple.style.left = Math.max(0, Math.min(rect.width, clientX - rect.left)) + 'px';
    ripple.style.top = Math.max(0, Math.min(rect.height, clientY - rect.top)) + 'px';
    control.appendChild(ripple);
    window.setTimeout(function () { ripple.remove(); }, 700);
  }

  function haptic() {
    try {
      if (window.FlorameHaptics && typeof window.FlorameHaptics.tap === 'function') {
        window.FlorameHaptics.tap('light');
      }
    } catch (_error) {
      // Haptics are enhancement-only.
    }
  }

  function onPointerMove(event) {
    if (event.pointerType === 'touch') return;
    var surface = event.target && event.target.closest ? event.target.closest(SURFACE_SELECTOR) : null;
    if (!surface) return;
    queueLiquidLight(surface, event.clientX, event.clientY);
  }

  function clearPressState() {
    if (pressedControl) pressedControl.classList.remove('is-liquid-pressed');
    if (pressedSurface) pressedSurface.classList.remove('is-liquid-pressed');
    pressedControl = null;
    pressedSurface = null;
  }

  function onPointerOut(event) {
    var surface = event.target && event.target.closest ? event.target.closest(SURFACE_SELECTOR) : null;
    if (!surface || surface.contains(event.relatedTarget)) return;
    surface.style.setProperty('--glass-tilt-x', '0deg');
    surface.style.setProperty('--glass-tilt-y', '0deg');
  }

  function onPointerDown(event) {
    if (event.button != null && event.button !== 0) return;

    var surface = event.target && event.target.closest ? event.target.closest(SURFACE_SELECTOR) : null;
    if (surface) {
      queueLiquidLight(surface, event.clientX, event.clientY);
      pressedSurface = surface;
      pressedSurface.classList.add('is-liquid-pressed');
    }

    pressedControl = event.target && event.target.closest ? event.target.closest(PRESS_SELECTOR) : null;
    if (pressedControl) pressedControl.classList.add('is-liquid-pressed');

    var control = event.target && event.target.closest ? event.target.closest(RIPPLE_SELECTOR) : null;
    if (!control) return;
    createRipple(control, event.clientX, event.clientY);
    haptic();
  }

  function syncHeadingCount() {
    countFrame = 0;
    var badge = document.querySelector('.florame-cart-heading__count');
    if (!badge) return;

    var total = Array.prototype.reduce.call(
      document.querySelectorAll(CART_ROOT_SELECTOR + ' quantity-input input'),
      function (sum, input) {
        if (input.closest('.is-delivery-service')) return sum;
        var value = Number.parseInt(input.value, 10);
        return sum + (Number.isFinite(value) && value > 0 ? value : 0);
      },
      0
    );
    var english = String(document.documentElement.lang || '').toLowerCase().indexOf('en') === 0;
    var unit = english
      ? (total === 1 ? 'item' : 'items')
      : (total === 1 ? 'articolo' : 'articoli');

    var previous = badge.dataset.liquidCount;
    // Avoid redundant live-region writes, including themes that place the
    // heading inside the observed cart contents.
    if (badge.textContent !== String(total)) badge.textContent = String(total);
    badge.dataset.liquidCount = String(total);
    badge.setAttribute('aria-label', total + ' ' + unit);

    if (previous != null && previous !== String(total) && !reducedMotion()) {
      badge.classList.remove('is-count-updating');
      void badge.offsetWidth;
      badge.classList.add('is-count-updating');
      window.clearTimeout(countAnimationTimer);
      countAnimationTimer = window.setTimeout(function () {
        badge.classList.remove('is-count-updating');
      }, 460);
    }
  }

  function queueHeadingCountSync() {
    if (countFrame) return;
    countFrame = window.requestAnimationFrame(syncHeadingCount);
  }

  function onCheckoutTrustFallback(event) {
    var trust = event.target && event.target.closest
      ? event.target.closest('[data-florame-checkout-trust]')
      : null;
    if (!trust || trust.dataset.interactionBound === '1') return;

    var explainId = trust.getAttribute('aria-controls') || 'florameCheckoutTrustExplain';
    var explain = document.getElementById(explainId);
    if (!explain) return;

    var open = trust.getAttribute('aria-expanded') !== 'true';
    trust.setAttribute('aria-expanded', open ? 'true' : 'false');
    explain.setAttribute('aria-hidden', open ? 'false' : 'true');
    trust.classList.toggle('is-inspected', open);
    haptic();
  }

  function onCartBack(event) {
    var backLink = event.target && event.target.closest
      ? event.target.closest('[data-florame-cart-back]')
      : null;
    if (!backLink) return;

    event.preventDefault();
    var fallback = backLink.href;
    var currentUrl = window.location.href;
    if (window.history.length <= 1) {
      window.location.assign(fallback);
      return;
    }

    window.history.back();
    window.setTimeout(function () {
      if (window.location.href === currentUrl) window.location.assign(fallback);
    }, 700);
  }

  function markReady() {
    if (!isCartPage()) return;
    document.documentElement.setAttribute('data-florame-cart-liquid', 'v628');
    document.body.classList.add('florame-cart-liquid-ready');
    queueHeadingCountSync();
  }

  function init() {
    if (!isCartPage()) return;
    markReady();
    window.requestAnimationFrame(playInitialEntrance);
    document.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('pointerout', onPointerOut, { passive: true });
    document.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerup', clearPressState, { passive: true });
    window.addEventListener('pointercancel', clearPressState, { passive: true });
    window.addEventListener('blur', clearPressState);
    document.addEventListener('click', onCheckoutTrustFallback);
    document.addEventListener('click', onCartBack);
    document.addEventListener('change', function (event) {
      if (event.target && event.target.matches(CART_ROOT_SELECTOR + ' quantity-input input')) {
        queueHeadingCountSync();
      }
    });

    var cartRoot = document.querySelector(CART_ROOT_SELECTOR);
    if (cartRoot && window.MutationObserver) {
      new MutationObserver(queueHeadingCountSync).observe(cartRoot, { childList: true, subtree: true });
    }

    document.addEventListener('florame:cart-updated', markReady);
    document.addEventListener('cart:updated', markReady);
    document.addEventListener('essential-upsell-cart-updated', markReady);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
